import { styled } from '@mui/material';
import { useEffect, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Box from 'shell/components/Box';
import Button from 'shell/components/Button';
import { DialogActions, DialogContent, DialogTitle } from 'shell/components/Dialog';
import Divider from 'shell/components/Divider';
import Icon from 'shell/components/Icon';
import IconButton from 'shell/components/IconButton';
import ResponsiveDialog from 'shell/components/ResponsiveDialog';
import Stack from 'shell/components/Stack';
import Text from 'shell/components/Text';
import TextArea from 'shell/components/TextArea';
import {
    hasEvidenceSuggestion,
    isEvidenceInstructionTooLong,
    toggleEvidenceSuggestion,
} from 'utils/assignmentSubmissionTypes';
import { CUSTOM_CONTENT_WORD_COUNT_LIMIT } from 'utils/constants';
import EVIDENCE_TYPE_DETAILS from './evidenceTypeDetails';

const EvidencePreferencesDialog = ({ type, preferences, open = true, disabled = false, onClose, onSave }) => {
    const { t } = useTranslation('assignmentSettings');
    const titleId = useId();
    const descriptionId = useId();
    const [draft, setDraft] = useState({ flag_instructions: '', ignore_instructions: '' });
    const config = EVIDENCE_TYPE_DETAILS[type];

    useEffect(() => {
        if (open)
            setDraft({
                flag_instructions: preferences?.flag_instructions || '',
                ignore_instructions: preferences?.ignore_instructions || '',
            });
    }, [open, preferences]);

    if (!config) return null;
    const hasErrors = Object.values(draft).some(isEvidenceInstructionTooLong);
    const save = () => {
        if (!disabled && !hasErrors) onSave(draft);
    };

    return (
        <EvidenceResponsiveDialog
            open={open}
            scroll={'paper'}
            aria-labelledby={titleId}
            aria-describedby={descriptionId}
            onClose={onClose}
        >
            <DialogTitle
                sx={{ px: { mobile: 4, tablet: 7 }, py: 3, display: 'flex', flexDirection: 'column', gap: 1.5 }}
            >
                <Stack direction={'row'} alignItems={'center'} spacing={3}>
                    <Text id={titleId} component={'h2'} variant={'h6'} weight={'medium'} sx={{ flex: 1 }}>
                        {t(`evidence.types.${type}.title`, config.title)}
                    </Text>
                    <IconButton
                        aria-label={t('evidence.close', 'Close evidence preferences')}
                        kind={'exit'}
                        size={'medium'}
                        variant={'square'}
                        onClick={onClose}
                    />
                </Stack>
                <Text id={descriptionId} color={'light'}>
                    {t(
                        'evidence.explanation',
                        'These decide what the AI brings you as evidence. They never decide the score, the rubric does.'
                    )}
                </Text>
            </DialogTitle>
            <Divider />
            <DialogContent sx={{ px: { mobile: 4, tablet: 7 }, py: 3 }}>
                <Stack spacing={6}>
                    <Stack
                        direction={'row'}
                        alignItems={'center'}
                        spacing={3}
                        sx={{
                            alignSelf: 'flex-start',
                            borderRadius: '40px',
                            px: 3,
                            py: 2,
                            background: (theme) => theme.palette.ensightful.primary[50],
                        }}
                    >
                        <Icon
                            kind={config.icon}
                            sx={{ fontSize: '1.5rem', color: (theme) => theme.palette.ensightful.neutral[700] }}
                        />
                        <Text weight={'medium'}>{t(`evidence.types.${type}.label`, config.label)}</Text>
                    </Stack>
                    {['flag', 'ignore'].map((kind, index) => {
                        const field = `${kind}_instructions`;
                        const label =
                            kind === 'flag'
                                ? t('evidence.flag-label', 'Should the AI flag these items?')
                                : t('evidence.ignore-label', 'What the AI should not flag');
                        const error = isEvidenceInstructionTooLong(draft[field]);
                        const suggestions = config[kind].map((fallback, suggestionIndex) =>
                            t(`evidence.types.${type}.${kind}.${suggestionIndex}`, fallback)
                        );
                        return (
                            <Stack key={kind} spacing={3}>
                                {index > 0 && <Divider sx={{ mb: 3 }} />}
                                <TextArea
                                    id={`${titleId}-${kind}`}
                                    label={label}
                                    aria-label={label}
                                    inputProps={{ 'aria-label': label, 'aria-invalid': error }}
                                    rows={6}
                                    value={draft[field]}
                                    error={error}
                                    disabled={disabled}
                                    helperText={
                                        error
                                            ? t(
                                                  'evidence.length-error',
                                                  'Use no more than {{limit}} words and 100,000 characters.',
                                                  { limit: CUSTOM_CONTENT_WORD_COUNT_LIMIT }
                                              )
                                            : undefined
                                    }
                                    sx={{
                                        '& textarea': {
                                            height: '128px',
                                            minHeight: '128px',
                                            maxHeight: '128px',
                                            padding: '12px 16px',
                                        },
                                        '& label': { fontWeight: 500, mb: 2 },
                                    }}
                                    onChange={(event) =>
                                        setDraft((current) => ({ ...current, [field]: event.target.value }))
                                    }
                                />
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                                    {suggestions.map((suggestion, suggestionIndex) => {
                                        const selected = hasEvidenceSuggestion(draft[field], suggestion, suggestions);
                                        return (
                                            <Button
                                                key={suggestionIndex}
                                                aria-pressed={selected}
                                                color={selected ? 'primary' : 'neutral'}
                                                variant={'secondary'}
                                                size={'medium'}
                                                disabled={disabled}
                                                sx={{
                                                    maxWidth: '100%',
                                                    px: 3,
                                                    py: 2,
                                                    textAlign: 'left',
                                                    ...(selected && {
                                                        background: (theme) => theme.palette.ensightful.primary[50],
                                                    }),
                                                }}
                                                onClick={() =>
                                                    setDraft((current) => ({
                                                        ...current,
                                                        [field]: toggleEvidenceSuggestion(
                                                            current[field],
                                                            suggestion,
                                                            suggestions
                                                        ),
                                                    }))
                                                }
                                            >
                                                {suggestion}
                                            </Button>
                                        );
                                    })}
                                </Box>
                            </Stack>
                        );
                    })}
                </Stack>
            </DialogContent>
            <Divider />
            <DialogActions sx={{ gap: 2.5, px: { mobile: 4, tablet: 7 } }}>
                <Button color={'neutral'} variant={'tertiary'} size={'medium'} onClick={onClose}>
                    {t('evidence.cancel', 'Cancel')}
                </Button>
                <Button size={'medium'} disabled={disabled || hasErrors} onClick={save}>
                    {t('evidence.done', 'Done')}
                </Button>
            </DialogActions>
        </EvidenceResponsiveDialog>
    );
};

const EvidenceResponsiveDialog = styled(ResponsiveDialog)(({ theme }) => ({
    '& .MuiPaper-root > .MuiBox-root': {
        padding: 0,
    },
    '& .MuiDialog-paper': {
        width: 838,
        maxWidth: `calc(100% - ${theme.spacing(8)})`,
        margin: theme.spacing(4),
    },
}));

export default EvidencePreferencesDialog;
